/**
 * Import ingestion refusals, bounds and format damage (docs/19 §5c–§5f, §24c).
 *
 * WHY THIS IS A CONTRACT AND NOT PART OF THE PARSER. A refusal has to be
 * renderable by the workbench, and the workbench runs in a browser. If the
 * refusal shape lived beside `yauzl` and `sax`, importing it would drag a ZIP
 * reader and an XML parser into the web bundle for the sake of a string union.
 * So the VOCABULARY lives here — pure types and zod, no I/O, no parser — and
 * `apps/api/src/services/imports/ingest` is the only place that knows how to
 * produce one.
 *
 * TWO RULES THIS FILE EXISTS TO ENFORCE, both from docs/19:
 *
 *   1. **A refusal names the bound and the observed value** (§5d). Not "file
 *      too large" — `per_file_compressed_bytes`, allowed 26214400, observed
 *      31457280. A person who cannot see which bound they crossed cannot fix
 *      their file.
 *   2. **A refusal never carries a raw source value.** It may name a PART PATH
 *      (`xl/vbaProject.bin`), a column, a row number or a character — never the
 *      contents of a cell. Staged values are classified data (docs/19 §3c) and
 *      a diagnostic is not an exemption from that.
 *
 * Phase 3 (docs/19 §20) is the ingestion library only. Nothing here describes a
 * route, a staging table or an execution outcome.
 */

import { z } from 'zod';

/**
 * Every bound docs/19 §5d declares, by name.
 *
 * The names are the contract: a refusal carries one of these strings, and the
 * test for each bound asserts that exact name came back. Spelling a bound in
 * prose instead would make the assertion a substring match on a sentence.
 */
export const INGEST_LIMIT_NAMES = [
  'per_file_compressed_bytes',
  'package_compressed_bytes',
  'per_file_decompressed_bytes',
  'compression_ratio',
  'parts_per_package',
  'relationships_per_package',
  'worksheets_per_workbook',
  'rows_per_worksheet',
  'columns_per_worksheet',
  'cells_per_run',
  'shared_strings',
  'single_string_length',
  'element_depth',
  'csv_line_length',
  'files_per_package',
  'parse_wall_clock_ms',
] as const;

export type IngestLimitName = (typeof INGEST_LIMIT_NAMES)[number];

export const ingestLimitNameSchema = z.enum(INGEST_LIMIT_NAMES);

/**
 * Why a package or file was refused.
 *
 * `limit_exceeded` is the one code that always carries a `limit`; the rest are
 * structural refusals from §5b's ordered checks and §5c's allowlist. They are
 * separate codes rather than one `invalid_package` because the person fixing
 * the file needs to know whether they have a macro, a formula or a stray
 * printer-settings blob — three different actions.
 */
export const INGEST_REFUSAL_CODES = [
  /** A declared bound was crossed; `limit`, `allowed` and `observed` are set. */
  'limit_exceeded',
  /** Extension or magic bytes are not what the declared format requires (§5b). */
  'signature_mismatch',
  /** A `.xls`, workbook or archive renamed `.csv` (§5b's OLE/zip sniff). */
  'disguised_binary',
  /**
   * The ZIP container itself is unreadable — a truncated or corrupt central
   * directory, a multi-disk archive, an encrypted entry, a malformed extra
   * field. Distinct from `signature_mismatch` (the first four bytes were right)
   * and from `unsafe_part_path` (a readable name that escapes the package).
   */
  'container_malformed',
  /** A part whose content type is not on §5c's allowlist. */
  'part_not_allowed',
  /**
   * Two physical entries claim the same part path (RM-088).
   *
   * A package with two `xl/workbook.xml` entries has no single answer to "what
   * does the workbook say", and any reader that picks one — first, last, or
   * whichever the map happened to keep — inspects a different document from the
   * one a second reader would. The ambiguity is refused rather than resolved.
   */
  'duplicate_part',
  /**
   * `[Content_Types].xml` declares a type outside §5c's allowlist (RM-088).
   *
   * Separate from `part_not_allowed`, which is about the PATH: a macro-enabled
   * workbook declared at the ordinary `xl/workbook.xml` path passes every path
   * rule and is still a macro workbook.
   */
  'content_type_not_allowed',
  /** A relationship whose internal target is not an allowlisted, present part. */
  'relationship_target_not_allowed',
  /**
   * `[Content_Types].xml` declares the same part or extension twice (C4).
   *
   * Distinct from `content_type_not_allowed`, which is about WHAT was declared.
   * This is about a package that declares a thing to be two things: a macro
   * workbook followed by an ordinary one for the same part has no single answer,
   * and a reader that keeps the last declaration hides the first.
   */
  'duplicate_content_type',
  /**
   * One `_rels` part declares the same `Id` twice (C5).
   *
   * Two relationships sharing `rId1` make every `r:id` reference ambiguous, and
   * last-one-wins silently gives one worksheet another's identity.
   */
  'duplicate_relationship_id',
  /** `../`, an absolute path, or a backslash-separated path (§5c). */
  'unsafe_part_path',
  /** Any macro part or macro content type (§5c). */
  'macro_content',
  /** `<f>` anywhere in the package, on any worksheet, hidden or not (§5c). */
  'formula_element',
  /** A cached `<v>` on a cell that also carried `<f>` — attacker-chosen (§5c). */
  'cached_formula_value',
  /** `externalReference` in the workbook (§5c). */
  'external_reference',
  /** Any relationship whose target leaves the package, including http(s) (§5c). */
  'external_relationship',
  /** A hyperlink relationship — refused even when internal-looking (§5c). */
  'hyperlink_relationship',
  /** `<!DOCTYPE` or any entity declaration in any part (§5c). */
  'xml_doctype',
  /** Malformed XML in an allowlisted part. */
  'xml_malformed',
  /** The workbook declares a sheet whose part is missing. */
  'missing_part',
  /** UTF-16 detected by BOM; §5f refuses rather than transcoding silently. */
  'utf16_not_supported',
  /** Invalid UTF-8; the refusal carries the byte offset. */
  'invalid_utf8',
  /**
   * A codepoint XML 1.0 forbids — NUL and the other C0 controls (RM-091).
   *
   * Not `xml_malformed`: the markup is well formed and the parser is content.
   * The character is outside XML's `Char` production, and passing it through
   * would be the silent acceptance §5f refuses on the CSV side.
   */
  'invalid_xml_character',
  /** The header parsed to one column but contains a tab or semicolon (§5f). */
  'delimiter_not_comma',
  /** RFC 4180 quoting broken — an unterminated quoted field. */
  'malformed_quoting',
  /**
   * A `"` inside an unquoted field (RFC 4180 §2 excludes it). CR-014: this and
   * the next were both reported as `malformed_quoting`, whose sentence says a
   * quoted field "is never closed" — wrong for both, and it sent the person
   * looking for a missing quote that was not missing.
   */
  'stray_quote',
  /** Text after a closing quote and before the next comma or line end. */
  'text_after_closing_quote',
  /** The file held no header row at all. */
  'empty_file',
  /** The parse exceeded its wall-clock budget (§24c). */
  'parse_budget_exceeded',
] as const;

export type IngestRefusalCode = (typeof INGEST_REFUSAL_CODES)[number];

export const ingestRefusalCodeSchema = z.enum(INGEST_REFUSAL_CODES);

/**
 * One refusal, in the shape the workbench renders and the tests assert.
 *
 * `part` is a package path or a CSV location — never a cell's contents. There
 * is deliberately no `value` field: adding one is how a diagnostic becomes an
 * exfiltration channel for the classified data §3c isolates.
 */
export interface IngestRefusalDetail {
  readonly code: IngestRefusalCode;
  /** Set for every `limit_exceeded`, absent otherwise. */
  readonly limit?: IngestLimitName | undefined;
  /** The declared ceiling, in the bound's own unit. */
  readonly allowed?: number | undefined;
  /** What was actually measured, in the same unit. */
  readonly observed?: number | undefined;
  /** The package part, worksheet name or CSV position the refusal points at. */
  readonly part?: string | undefined;
  /** A byte offset, for `invalid_utf8`. */
  readonly byteOffset?: number | undefined;
}

export const ingestRefusalDetailSchema: z.ZodType<IngestRefusalDetail> = z.object({
  code: ingestRefusalCodeSchema,
  limit: ingestLimitNameSchema.optional(),
  allowed: z.number().int().nonnegative().optional(),
  observed: z.number().int().nonnegative().optional(),
  part: z.string().max(512).optional(),
  byteOffset: z.number().int().nonnegative().optional(),
});

/**
 * The format damage §5f detects and REPORTS — never repairs.
 *
 * Each of these is a value Excel has probably already changed before the file
 * reached us. The workbench shows them per column and leaves the source value
 * exactly as it found it, because a silent repair is a second, invisible edit.
 */
export const FORMAT_DAMAGE_KINDS = [
  /** A ZIP column holding fewer than five digits — a leading zero Excel ate. */
  'zip_truncated',
  /** An identifier rendered as `1.23E+15`. */
  'scientific_notation',
  /** A numeric identifier at or beyond 2^53, where precision is already gone. */
  'precision_loss',
  /** A date that is not ISO-8601 and may be locale-ordered. */
  'ambiguous_date',
  /** Mixed or invalid encoding within one column. */
  'encoding_damage',
] as const;

export type FormatDamageKind = (typeof FORMAT_DAMAGE_KINDS)[number];

export const formatDamageKindSchema = z.enum(FORMAT_DAMAGE_KINDS);

/**
 * A per-column damage warning. Counts and a sample ROW number, never a value.
 */
export interface FormatDamageWarning {
  readonly kind: FormatDamageKind;
  /** Zero-based column index within the worksheet or CSV. */
  readonly column: number;
  /** How many cells in that column matched. */
  readonly affectedCells: number;
  /** The first row number where it was seen, so a person can go look. */
  readonly firstRow: number;
}

export const formatDamageWarningSchema: z.ZodType<FormatDamageWarning> = z.object({
  kind: formatDamageKindSchema,
  column: z.number().int().nonnegative(),
  affectedCells: z.number().int().positive(),
  firstRow: z.number().int().positive(),
});

/**
 * THE LIMIT THE SCREEN MUST STATE (§5f), kept here so every surface says the
 * same thing and nobody writes a cheerier version of it.
 *
 * It is a sentence, not a template: there is no interpolation slot, because a
 * slot is where a cell value eventually gets put.
 */
export const FORMAT_DAMAGE_LIMIT_NOTICE =
  'These checks cannot detect every value a spreadsheet application may already ' +
  'have changed. Upload XLSX rather than CSV to avoid the whole class.';
